import React from "react";
import { Box, Typography } from "@mui/material";

interface TimelineHeaderProps {
  weeks: Array<{
    label: string;
    start: Date;
    end: Date;
    month?: string;
  }>;
  viewMode: "today" | "weeks" | "months" | "quarters";
}

export const TimelineHeader: React.FC<TimelineHeaderProps> = ({
  weeks,
  viewMode,
}) => {
  // Group weeks by month for display
  const groupedByMonth = weeks.reduce((acc, week) => {
    const month = week.month || "";
    if (!acc[month]) {
      acc[month] = [];
    }
    acc[month].push(week);
    return acc;
  }, {} as Record<string, typeof weeks>);

  const getDaysInPeriod = (period: (typeof weeks)[0]) => {
    return (
      Math.ceil(
        (period.end.getTime() - period.start.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1
    );
  };

  return (
    <Box sx={{ minWidth: "max-content" }}>
      {/* Month Headers */}
      <Box
        sx={{
          display: "flex",
          borderBottom: "1px solid #E5E7EB",
          bgcolor: "#F3F4F6",
        }}
      >
        {Object.entries(groupedByMonth).map(([month, periods]) => {
          const totalDays = periods.reduce(
            (sum, period) => sum + getDaysInPeriod(period),
            0
          );

          return (
            <Box
              key={month}
              sx={{
                minWidth: `${totalDays * 40}px`,
                borderRight: "1px solid #E5E7EB",
                py: 1,
                px: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#374151",
                }}
              >
                {month}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Week/Period Headers */}
      <Box sx={{ display: "flex", bgcolor: "#FAFAFA" }}>
        {weeks.map((period, idx) => {
          const days = getDaysInPeriod(period);

          return (
            <Box
              key={idx}
              sx={{
                minWidth: `${days * 40}px`,
                borderRight: "1px solid #E5E7EB",
                py: 1.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  fontWeight: 500,
                  fontSize: 12,
                  color: "#6B7280",
                }}
              >
                {period.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  fontSize: 10,
                  color: "#9CA3AF",
                  mt: 0.25,
                }}
              >
                {period.start.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}{" "}
                -{" "}
                {period.end.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Day Numbers */}
      <Box sx={{ display: "flex", borderBottom: "1px solid #E5E7EB" }}>
        {weeks.map((period, idx) => {
          const days = getDaysInPeriod(period);
          const dayNumbers = Array.from({ length: days }, (_, i) => {
            const date = new Date(period.start);
            date.setDate(date.getDate() + i);
            return date;
          });

          return (
            <Box
              key={idx}
              sx={{ display: "flex", borderRight: "1px solid #E5E7EB" }}
            >
              {dayNumbers.map((date, dayIdx) => {
                const isToday =
                  date.toDateString() === new Date().toDateString();
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                return (
                  <Box
                    key={dayIdx}
                    sx={{
                      width: 40,
                      py: 0.75,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      bgcolor: isWeekend ? "#F9FAFB" : "white",
                      borderRight:
                        dayIdx < dayNumbers.length - 1
                          ? "1px solid #F3F4F6"
                          : "none",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 10,
                        fontWeight: isToday ? 600 : 400,
                        color: isToday
                          ? "#3B82F6"
                          : isWeekend
                          ? "#9CA3AF"
                          : "#6B7280",
                        bgcolor: isToday ? "#DBEAFE" : "transparent",
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {date.getDate()}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
